/**
 * Suggested questions across every agreement. They are Nami's chips on the
 * contracts list (they used to be an "Ask across all agreements" sheet). The
 * auto-renewal answer is computed from today's date so it stays correct when the
 * demo is run on a different day.
 */
import type { AskCitation, ScriptedAnswer } from '../../ask/types';
import { clausesForContract } from '../../data/clauses';
import { CONTRACT_LIST, noticeDeadline } from '../../data/contracts';
import {
  addDays,
  daysUntil,
  fmtDate,
  fmtRelative,
  parseIsoDate,
} from '../../data/dates';
import { getSupplier } from '../../data/suppliers';
import type { Contract } from '../../data/types';
import { REPOSITORY_STATS } from './contracts-data';
import { cite } from './ask-documents';

const AUTO_RENEW_WINDOW_DAYS = 90;

/** Extra context for an auto-renewing agreement the story cares about. */
const AUTO_RENEW_NOTES: Partial<Record<string, string>> = {
  'EA-STRT-2024-001':
    'Consumption is tracking 18% under the $12M commitment, so this is the one to renegotiate.',
};

interface AutoRenewItem {
  contract: Contract;
  renewalDate: Date;
  deadline: Date;
  section: string;
  quote: string;
}

/** Auto-renewing agreements whose renewal clause is backed by the text. */
function autoRenewItems(): AutoRenewItem[] {
  const items: AutoRenewItem[] = [];
  for (const contract of CONTRACT_LIST) {
    const deadline = noticeDeadline(contract);
    if (!deadline) continue;
    const clause = clausesForContract(contract.id).find(
      (entry) => entry.kind === 'auto_renewal',
    );
    if (!clause) continue;
    items.push({
      contract,
      renewalDate: addDays(parseIsoDate(contract.endDate), 1),
      deadline,
      section: clause.section,
      quote: clause.quote,
    });
  }
  return items;
}

function describeItem(item: AutoRenewItem): string {
  const { contract } = item;
  const supplier = getSupplier(contract.supplierId);
  const months = contract.autoRenew.termMonths ?? 12;
  const note = AUTO_RENEW_NOTES[contract.id];
  return `- **${supplier.shortName} (${contract.id}):** auto-renews for ${months} months on ${fmtDate(item.renewalDate)} unless notice is given by **${fmtDate(item.deadline)}**, ${fmtRelative(item.deadline)} (§${item.section}).${note ? ` ${note}` : ''}`;
}

export function buildAutoRenewAnswer(): ScriptedAnswer {
  const items = autoRenewItems();
  const renewing = items.filter((item) => {
    const days = daysUntil(item.renewalDate);
    return days >= 0 && days <= AUTO_RENEW_WINDOW_DAYS;
  });
  const deadlines = items.filter((item) => {
    const days = daysUntil(item.deadline);
    const renewalDays = daysUntil(item.renewalDate);
    return (
      days >= 0 &&
      days <= AUTO_RENEW_WINDOW_DAYS &&
      renewalDays > AUTO_RENEW_WINDOW_DAYS
    );
  });

  const lines: string[] = [];
  if (renewing.length === 0) {
    lines.push('No strategic agreement auto-renews in the next 90 days.');
  } else {
    lines.push(
      `${renewing.length} ${renewing.length === 1 ? 'agreement renews' : 'agreements renew'} automatically in the next 90 days.`,
    );
    lines.push('', ...renewing.map(describeItem));
  }
  if (deadlines.length > 0) {
    lines.push(
      '',
      `${deadlines.length === 1 ? 'One notice deadline falls' : `${deadlines.length} notice deadlines fall`} inside the window, ahead of a later renewal:`,
      '',
      ...deadlines.map(describeItem),
    );
  }
  lines.push(
    '',
    `Across the repository there are ${REPOSITORY_STATS.renewalsIn90Days} renewals and notice deadlines in the next 90 days. The rest are on lower-value agreements and are queued in the Renewals tab.`,
  );

  const citations: AskCitation[] = [...renewing, ...deadlines].map((item) =>
    cite(item.contract.id, item.section, item.quote),
  );

  return {
    question: 'Which agreements auto-renew in the next 90 days?',
    aliases: [
      'Which contracts auto-renew in the next 90 days?',
      'What renews in the next 90 days?',
      'Which agreements have a notice deadline in the next 90 days?',
    ],
    thinking: [
      `Scanning ${REPOSITORY_STATS.curatedAgreements} agreements for renewal terms`,
      'Calculating notice deadlines from end dates and notice periods',
      'Checking each renewal clause against the agreement text',
    ],
    answer: lines.join('\n'),
    citations,
  };
}

const FREIGHT: ScriptedAnswer = {
  question: 'Which suppliers can charge us freight?',
  aliases: [
    'Which suppliers can bill freight?',
    'Who can charge us for freight?',
    'Which agreements let the supplier charge freight?',
  ],
  thinking: [
    'Reading the delivery terms in every supply agreement',
    'Checking which agreements leave freight with Pillar',
  ],
  answer: `Only **Kaisei** can charge freight on top of its price. Every other supply agreement delivers with freight included, so the supplier carries freight to the Pillar DC.

- **Kaisei (SA-KAIS-2025-003):** $448.00 per 100 ft, collected by Pillar in Osaka. Pillar arranges and pays carriage, and the price excludes freight, insurance and import duties, which puts the delivered cost near $462 per 100 ft (§2.1).
- **Corvane, Aurelia, Northgate, Hollis, Meridian, Polaris, Clearwell and Vitrex:** delivered with freight included. No separate freight or delivery charge is payable (Corvane §5.1 and §5.2, Aurelia §5.2, Northgate §4.1, Hollis §4.1).
- **Lumen:** delivered to the Hayward DC with duties and tariffs paid. Lumen bears freight, insurance and customs clearance (§5.1).
- **Brightpak** has no agreement and buys on spot POs, so freight is whatever each PO says. Its LED high-bay bid adds $2.80 per fixture.
- **Transcontinental** charges freight by design. It is Pillar's carrier, and its charges are limited to base freight, a capped fuel surcharge and listed extra charges (§5.1).`,
  citations: [
    cite('SA-KAIS-2025-003', '2.1'),
    cite('SA-CORV-2024-017', '5.2'),
    cite('SA-AURE-2024-009', '5.2'),
    cite('SA-NGTE-2024-012', '4.1'),
    cite('SA-HOLL-2024-021', '4.1'),
    cite('SA-LUMN-2025-002', '5.1'),
    cite('MSA-TRAN-2025-006', '5.1'),
  ],
};

const TARIFFS: ScriptedAnswer = {
  question: 'Where can suppliers pass tariffs through to us?',
  aliases: [
    'Which suppliers can pass tariffs on to us?',
    'Where are we exposed to tariffs?',
    'Which agreements allow tariff surcharges?',
  ],
  thinking: [
    'Reading duties, tariffs and price clauses in every agreement',
    'Checking the delivery terms to see who clears imports',
    'Checking spot-PO suppliers with no agreement',
  ],
  answer: `**No agreement lets a supplier add a tariff to its price.** Where the delivery terms leave import duty with Pillar, Pillar pays it directly.

- **Lumen (duties and tariffs paid by the supplier):** the strongest protection. Lumen bears all duties and tariffs, including new or increased ones, and may not invoice them as a surcharge or pass-through (§5.3). A tariff change is not force majeure (§12.3). Invoice LB-26-3391 breaches this.
- **Kaisei (Pillar collects in Osaka):** the price excludes import duties, so Pillar bears the duty. Delivered cost is $462.30 per 100 ft against a $448.00 unit price (§2.1).
- **Corvane and Aurelia (freight included):** prices carry no surcharges (Corvane §3.2), and each supplier is responsible only for the taxes, duties and fees on manufacture (Corvane §4.3, Aurelia §4.4). Import clearance and duty on cross-border shipments sit with Pillar.
- **Brightpak:** no agreement and spot POs only, so there is no contractual protection. Its LED high-bay bid carries a 30% tariff, $24.54 per fixture, and a forced-labor import risk on its aluminium supplier’s supplier.`,
  citations: [
    cite('SA-LUMN-2025-002', '5.3'),
    cite('SA-LUMN-2025-002', '12.3'),
    cite('SA-KAIS-2025-003', '2.1'),
    cite('SA-CORV-2024-017', '3.2'),
    cite('SA-CORV-2024-017', '4.3'),
    cite('SA-AURE-2024-009', '4.4'),
  ],
};

const LIABILITY: ScriptedAnswer = {
  question: 'Which agreements have a liability cap below 12 months of fees?',
  aliases: [
    'Which contracts cap liability below 12 months of fees?',
    'Where is our liability protection weakest?',
    'What are the liability caps across our agreements?',
  ],
  thinking: [
    'Reading limitation-of-liability clauses in every agreement',
    'Comparing each cap with 12 months of fees',
  ],
  answer: `Only one agreement caps liability below 12 months of fees: **Halbrook's master agreement**.

- **Halbrook master agreement (MSA-HALB-2023-011):** each side's liability is limited to the fees under the statement of work concerned (§9.3). For SOW-HALB-2026-03 that is $450,000, against about $3.8M a year of fees under the master agreement. Indemnities, confidentiality breaches, gross negligence and willful misconduct sit outside the cap.
- **Gently AI (MSA-GEN-2025-001):** exactly 12 months of fees (§9.1), with a separate 3x cap for data breaches (§9.3).
- **Stratus (EA-STRT-2024-001):** the greater of 12 months of fees or $12M (§9.3).
- **Corvane, Lumen and Aurelia:** no monetary cap. They only exclude indirect and consequential damages (Corvane §11.2, Lumen §10.2, Aurelia §11.2).
- **Transcontinental:** cargo liability at full value under federal law, the Carmack Amendment (§8.1).
- The short-form agreements set no monetary cap.`,
  citations: [
    cite('MSA-HALB-2023-011', '9.3'),
    cite('MSA-GEN-2025-001', '9.1'),
    cite('EA-STRT-2024-001', '9.3'),
    cite('SA-CORV-2024-017', '11.2'),
    cite('SA-LUMN-2025-002', '10.2'),
    cite('SA-AURE-2024-009', '11.2'),
    cite('MSA-TRAN-2025-006', '8.1'),
  ],
};

export function buildRepositorySuggestions(): ScriptedAnswer[] {
  return [buildAutoRenewAnswer(), FREIGHT, TARIFFS, LIABILITY];
}
